import { useEffect, useRef, useState } from 'react';
import { XIcon } from './icons.js';
import type { Classification, PiiFlag } from '@kennisbank/shared';

/**
 * ISO 27001 inline-edit cells for the Knowledgebase Admin table.
 *
 * Three cells share this file because they all live in the same row and
 * share the same save-via-PATCH pattern as InlineSpecialisationsCell —
 * extracting a single helper component for them would over-abstract; a
 * three-paragraph file is clearer than three separate files.
 *
 *  - ClassificationCell — single-select dropdown chip.
 *    public = grey, internal = brand, confidential = mosterdgeel, restricted = petrol.
 *    Picking confidential/restricted while accessGroups is empty surfaces
 *    a server-side validation error via the parent's saveError state — the
 *    parent decides whether to also auto-open the access-groups picker.
 *
 *  - AccessGroupsCell — multi-select chip. Day-one vocabulary is the
 *    `['confidential']` set fetched from `/api/v1/admin/knowledgebase/access-groups`.
 *
 *  - PiiFlagsCell — multi-select chip from the PiiFlag enum.
 */

const CLASSIFICATIONS: Classification[] = ['public', 'internal', 'confidential', 'restricted'];

const CHIP_STYLE: Record<Classification, string> = {
  public: 'bg-slate-100 text-slate-700 border-slate-300',
  internal: 'bg-brand-50 text-brand-700 border-brand-200',
  confidential: 'bg-mosterdgeel-50 text-mosterdgeel-700 border-mosterdgeel-300',
  restricted: 'bg-petrol-50 text-petrol-700 border-petrol-300',
};

const PII_FLAG_VALUES: PiiFlag[] = [
  'email_addresses',
  'phone_numbers',
  'salary_data',
  'contract_value',
  'employee_names',
  'customer_names',
];

const PII_FLAG_LABELS: Record<PiiFlag, string> = {
  email_addresses: 'Emails',
  phone_numbers: 'Phones',
  salary_data: 'Salary',
  contract_value: 'Contract value',
  employee_names: 'Employee names',
  customer_names: 'Customer names',
};

function useOutsideClose(open: boolean, close: () => void) {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent): void => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) close();
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open, close]);
  return wrapRef;
}

export function ClassificationCell({
  value,
  saving,
  onSave,
}: {
  value: Classification;
  saving: boolean;
  onSave: (next: Classification) => void | Promise<void>;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const wrapRef = useOutsideClose(open, () => setOpen(false));
  const current = value ?? 'internal';
  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={saving}
        className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium ${CHIP_STYLE[current]} disabled:opacity-50`}
      >
        {current}
      </button>
      {open && (
        <div className="absolute left-0 top-full z-20 mt-1 w-40 rounded-md border border-brand-200 bg-white p-1 shadow-lg">
          {CLASSIFICATIONS.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => {
                setOpen(false);
                if (c !== current) void onSave(c);
              }}
              className={`block w-full rounded px-2 py-1 text-left text-xs hover:bg-brand-50 ${
                c === current ? 'font-semibold' : ''
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function AccessGroupsCell({
  value,
  available,
  saving,
  onSave,
}: {
  value: string[];
  /** Available groups from `/api/v1/admin/knowledgebase/access-groups`. */
  available: string[];
  saving: boolean;
  onSave: (next: string[]) => void | Promise<void>;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const wrapRef = useOutsideClose(open, () => setOpen(false));
  const remaining = available.filter((g) => !value.includes(g));
  const remove = async (g: string): Promise<void> => {
    await onSave(value.filter((x) => x !== g));
  };
  const add = async (g: string): Promise<void> => {
    if (value.includes(g)) return;
    await onSave([...value, g]);
    setOpen(false);
  };
  return (
    <div ref={wrapRef} className="relative">
      <div className="flex flex-wrap items-center gap-1">
        {value.map((g) => (
          <span
            key={g}
            className="inline-flex items-center gap-1 rounded-full bg-mosterdgeel-50 px-2 py-0.5 text-[11px] font-medium text-mosterdgeel-700"
          >
            {g}
            <button
              type="button"
              onClick={() => void remove(g)}
              disabled={saving}
              aria-label={`Remove ${g}`}
              className="rounded-full text-mosterdgeel-700/70 hover:bg-mosterdgeel-100 hover:text-mosterdgeel-800 disabled:opacity-40"
            >
              <XIcon size={11} />
            </button>
          </span>
        ))}
        {remaining.length > 0 && (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            disabled={saving}
            className="inline-flex items-center gap-0.5 rounded-full border border-dashed border-brand-300 bg-white px-2 py-0.5 text-[11px] font-medium text-brand-700 hover:border-brand-500 hover:bg-brand-50 disabled:opacity-50"
          >
            + group
          </button>
        )}
        {value.length === 0 && !open && <span className="text-ink-900/30">—</span>}
      </div>
      {open && remaining.length > 0 && (
        <div className="absolute left-0 top-full z-20 mt-1 w-44 rounded-md border border-brand-200 bg-white p-1 shadow-lg">
          {remaining.map((g) => (
            <button
              key={g}
              type="button"
              onClick={() => void add(g)}
              className="block w-full rounded px-2 py-1 text-left text-xs hover:bg-brand-50"
            >
              {g}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function PiiFlagsCell({
  value,
  saving,
  onSave,
}: {
  value: PiiFlag[];
  saving: boolean;
  onSave: (next: PiiFlag[]) => void | Promise<void>;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const wrapRef = useOutsideClose(open, () => setOpen(false));
  const remaining = PII_FLAG_VALUES.filter((f) => !value.includes(f));
  return (
    <div ref={wrapRef} className="relative">
      <div className="flex flex-wrap items-center gap-1">
        {value.map((f) => (
          <span
            key={f}
            className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-700"
          >
            {PII_FLAG_LABELS[f] ?? f}
            <button
              type="button"
              onClick={() => void onSave(value.filter((x) => x !== f))}
              disabled={saving}
              aria-label={`Remove ${f}`}
              className="rounded-full text-slate-600 hover:bg-slate-200 disabled:opacity-40"
            >
              <XIcon size={11} />
            </button>
          </span>
        ))}
        {remaining.length > 0 && (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            disabled={saving}
            className="inline-flex items-center gap-0.5 rounded-full border border-dashed border-brand-300 bg-white px-2 py-0.5 text-[11px] font-medium text-brand-700 hover:border-brand-500 hover:bg-brand-50 disabled:opacity-50"
          >
            + PII
          </button>
        )}
        {value.length === 0 && !open && <span className="text-ink-900/30">—</span>}
      </div>
      {open && remaining.length > 0 && (
        <div className="absolute left-0 top-full z-20 mt-1 w-52 rounded-md border border-brand-200 bg-white p-1 shadow-lg">
          {remaining.map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => {
                setOpen(false);
                void onSave([...value, f]);
              }}
              className="block w-full rounded px-2 py-1 text-left text-xs hover:bg-brand-50"
            >
              {PII_FLAG_LABELS[f] ?? f}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
